import { useMemo } from "react";
import { createColumnHelper, useReactTable, getCoreRowModel, flexRender } from "@tanstack/react-table";
import { Trash2 } from "lucide-react";
import TextInput from "../TextInput";
import Button from "../Button";
import { withDerivedTierBoundaries } from "../../billing/TierTable";
import "./_tiered-pricing-field.scss";

const columnHelper = createColumnHelper();

/**
 * Generic editable tiered-pricing field: From/Unit price per row (To is
 * computed via `TierTable`'s own `withDerivedTierBoundaries` and read-only
 * - same "don't let To drift out of sync with the next tier's From" reason
 * as `TierTable` itself), add/remove tier rows - replaces
 * `@simplifyhq/simplify-ui-reusables`'s `FormInputTieredPricing`.
 *
 * Deliberately NOT built on `TierTable` directly - that component is
 * shaped for billing line items (`itemId`/`quantity`/`currencySign`,
 * computed Qty/Tier-price columns via `computeTierBreakdown`), none of
 * which exist for a generic form field. This reuses only the pure
 * boundary-derivation function, not the whole component.
 *
 * No live schema currently exercises "tieredPricing" (confirmed absent
 * from every real caller during this migration - `FormField.jsx` itself
 * notes "no bridged field in CompanyFormModal's schema needs tieredPricing
 * yet"), so this is built to the old component's `pricingTiers`/
 * `setPricingTiers`/`disableAddTier` shape as a best-effort mirror, not
 * validated against a concrete real-world caller. `initialData` (the old
 * component's seed-data prop) is dropped - every real call site already
 * defaulted it to `[]`, identical to `pricingTiers` starting empty.
 */
const TieredPricingField = ({ pricingTiers = [], setPricingTiers, disableAddTier = false, disabled = false }) => {
  // MUST be memoized, not computed inline - see TierTable's own docstring
  // for why: TanStack Table's internal auto-reset effects watch `data` BY
  // REFERENCE, so a fresh array every render (even with identical
  // contents) reads as "new data", retriggering those effects, which
  // re-renders this component, which computes another fresh array - an
  // infinite render loop with no state actually changing.
  const tiers = useMemo(() => withDerivedTierBoundaries(pricingTiers), [pricingTiers]);

  const updateTier = (index, key, value) => {
    const next = pricingTiers.map((tier, i) => (i === index ? { ...tier, [key]: value } : tier));
    setPricingTiers?.(next);
  };

  const removeTier = (index) => {
    setPricingTiers?.(pricingTiers.filter((_, i) => i !== index));
  };

  const addTier = () => {
    setPricingTiers?.([...(pricingTiers || []), { from: "", to: "", unitPrice: "" }]);
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor("from", {
        header: "From",
        cell: (info) => (
          <TextInput
            compact
            value={info.getValue()}
            onChange={(value) => updateTier(info.row.index, "from", value)}
            disabled={disabled}
          />
        ),
      }),
      columnHelper.accessor("to", {
        header: "To",
        cell: (info) => <TextInput compact value={info.getValue() === Infinity ? "∞" : info.getValue()} disabled />,
      }),
      columnHelper.accessor("unitPrice", {
        header: "Unit price",
        cell: (info) => (
          <TextInput
            compact
            value={info.getValue()}
            onChange={(value) => updateTier(info.row.index, "unitPrice", value)}
            disabled={disabled}
          />
        ),
      }),
      columnHelper.display({
        id: "actions",
        header: () => null,
        cell: (info) =>
          info.row.index > 0 &&
          !disabled && (
            <Button
              variant="ghost-danger"
              size="md"
              iconOnly
              icon={<Trash2 size={16} />}
              onClick={() => removeTier(info.row.index)}
              aria-label={`Remove tier ${info.row.index + 1}`}
            />
          ),
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disabled, pricingTiers]
  );

  const table = useReactTable({
    data: tiers,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (_row, index) => index,
  });

  return (
    <div className="tiered-pricing-field">
      <div className="tiered-pricing-field__table-wrapper">
        <table className="tiered-pricing-field__table">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>{flexRender(header.column.columnDef.header, header.getContext())}</th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!disableAddTier && !disabled && (
        <Button variant="ghost" size="md" className="tiered-pricing-field__add-button" onClick={addTier}>
          + Add tier
        </Button>
      )}
    </div>
  );
};

export default TieredPricingField;
